import { useCallback, useEffect, useRef, useState } from "react";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * TurnstileWidget — thin React wrapper around Cloudflare Turnstile.
 * - Renders the widget when `siteKey` is set; otherwise renders a friendly
 *   placeholder + auto-passes with the sentinel token "dev-bypass" so local
 *   dev without a Cloudflare key still works.
 * - Calls `onToken(token)` on solve; calls `onToken("")` on expire/error.
 */
export function TurnstileWidget({ siteKey, onToken, theme = "dark" }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(() => {
    if (document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) {
      setReady(true);
      return;
    }
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!siteKey) {
      onToken("dev-bypass");
      return;
    }
    load();
  }, [siteKey, load, onToken]);

  useEffect(() => {
    if (!siteKey || !ready || !ref.current) return;
    if (!window.turnstile) return;
    const widgetId = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      theme,
      callback: (token) => onToken(token),
      "expired-callback": () => onToken(""),
      "error-callback": () => onToken(""),
    });
    return () => {
      try { window.turnstile.remove(widgetId); } catch (err) { if (typeof console !== 'undefined') console.debug('components/TurnstileWidget.jsx: swallowed', err); }
    };
  }, [siteKey, ready, theme, onToken]);

  if (!siteKey) {
    return (
      <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-zinc-500"
           data-testid="turnstile-dev-mode">
        Bot check skipped — dev mode (no <code>REACT_APP_TURNSTILE_SITE_KEY</code>)
      </div>
    );
  }
  return <div ref={ref} data-testid="turnstile-widget" />;
}

export default TurnstileWidget;
